import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Phone, Sparkles, UtensilsCrossed } from "lucide-react";
import { DishList, VegLegend } from "@/components/DishList";
import { api, errText } from "@/lib/api";

export default function PublicMenu() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/public/menu/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errText(e)));
  }, [token]);

  if (error)
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" data-testid="public-menu-error">
        <UtensilsCrossed className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-4 text-sm text-muted-foreground">{error}</p>
      </div>
    );

  if (!data)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24">
        <p className="text-sm text-muted-foreground">Loading menu…</p>
      </div>
    );

  const today = data.week.find((d) => d.is_today);

  return (
    <div className="mx-auto max-w-3xl px-5 md:px-8 py-10" data-testid="public-menu-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">Food menu</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl">{data.stay.name}</h1>
      <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
        {[data.stay.locality, data.stay.city].filter(Boolean).length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> {[data.stay.locality, data.stay.city].filter(Boolean).join(", ")}
          </span>
        )}
        {data.stay.contact_phone && (
          <a href={`tel:${data.stay.contact_phone}`} className="inline-flex items-center gap-1.5 hover:underline" data-testid="public-menu-phone">
            <Phone className="h-3.5 w-3.5" /> {data.stay.contact_phone}
          </a>
        )}
      </p>

      {data.special_today && (
        <div className="mt-7 rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="public-menu-special-today">
          <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Special today
          </p>
          <p className="mt-2 font-display text-2xl">{data.special_today}</p>
        </div>
      )}

      <div className="mt-8 rounded-2xl border-2 border-foreground p-6 sm:p-8" data-testid="public-menu-today">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-lg md:text-lg">Today · {today?.label}</h2>
          <span className="text-[12px] text-muted-foreground">{data.today.date}</span>
        </div>
        {today?.weekday_special && (
          <p className="mt-2 text-[13px] font-semibold text-primary">Every {today.label}: {today.weekday_special}</p>
        )}
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {today?.meals.map((m) => (
            <div key={m.key} className="rounded-xl bg-muted/60 p-4" data-testid={`public-menu-today-${m.key}`}>
              <p className="flex items-center justify-between gap-2 text-sm font-bold">
                {m.name}
                {m.time && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                    <Clock className="h-3 w-3" /> {m.time}
                  </span>
                )}
              </p>
              <div className="mt-2">
                <DishList dishes={m.dishes} items={m.items || "Not set yet"} testid={`public-menu-dishes-${m.key}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <VegLegend />
        </div>
      </div>

      <h2 className="mt-12 font-display text-lg md:text-lg">Full week</h2>
      <div className="mt-5 space-y-4">
        {data.week.map((d) => (
          <div
            key={d.key}
            className={`rounded-2xl border p-5 transition-colors duration-200 ${d.is_today ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40"}`}
            data-testid={`public-menu-day-${d.key}`}
          >
            <p className="text-sm font-bold">
              {d.label}
              {d.is_today && <span className="ml-2 rounded-full bg-foreground px-2 py-0.5 text-[10px] font-bold text-background">TODAY</span>}
            </p>
            {d.weekday_special && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> {d.weekday_special}
              </p>
            )}
            <dl className="mt-3 grid gap-2.5 sm:grid-cols-2">
              {d.meals.map((m) => (
                <div key={m.key} className="text-[13px]">
                  <dt className="font-semibold">
                    {m.name}
                    {m.time && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">{m.time}</span>}
                  </dt>
                  <dd className="mt-1">
                    <DishList dishes={m.dishes} items={m.items} testid={`public-menu-dishes-${d.key}-${m.key}`} />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {data.upcoming_specials?.length > 0 && (
        <>
          <h2 className="mt-12 font-display text-lg md:text-lg">Coming up</h2>
          <ul className="mt-4 space-y-2" data-testid="public-menu-upcoming">
            {data.upcoming_specials.map((s) => (
              <li key={s.date} className="flex items-center gap-3 rounded-xl border border-border px-4 py-3 text-[13px]">
                <CalendarDays className="h-4 w-4 text-primary" />
                <span className="font-semibold">{s.date}</span>
                <span className="text-muted-foreground">{s.note}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {data.note && <p className="mt-10 rounded-xl bg-muted p-5 text-[13px] text-muted-foreground" data-testid="public-menu-note">{data.note}</p>}
    </div>
  );
}
